import { Injectable, inject } from '@angular/core';
import { DataSeries } from '../models/classes/data-series.class';
import { KeyValuePair } from '../models/classes/key-value-pair.class';
import { CurrencyFormatPipe } from '../pipes/currency-format.pipe';
import { TooltipOptions } from 'highcharts';
import topologyJson from '@highcharts/map-collection/custom/world-continents.geo.json';
import usTopology from '@highcharts/map-collection/countries/us/us-all.geo.json';
import { BRIGHT_GREEN, BURNT_ORANGE, CHARCOAL_GREY, CYAN, DEFAULT_COLORS, GROSVENOR_BLUE, LIGHT_GREY, MARIGOLD, NAVY, PERIWINKLE, RED_PLUM, ROYAL_BLUE, VIOLET, WHITE } from '@gcm/gcm-ui-angular';
export type ChartType = 'bar' | 'line' | 'map' | 'column' | 'funnel' | 'pie';

@Injectable({
  providedIn: 'root'
})
export class HighchartsService {
  private currencyFormat = inject(CurrencyFormatPipe);

  whiteColor = WHITE;
  defaultColors = DEFAULT_COLORS;
  continentWithMapCodeMapping = [{ continent: 'Europe', code: 'eu' }, { continent: 'North America', code: 'na' }, { continent: 'Asia', code: 'as' }, { continent: 'Other', code: 'other' }];
  colorCodesMappingWithContinent = [{ code: 'eu', color: PERIWINKLE }, { code: 'as', color: NAVY }, { code: 'na', color: GROSVENOR_BLUE }, { code: 'other', color: BURNT_ORANGE }, { code: 'noData', color: LIGHT_GREY }]
  usDivisionMapping = [
    { division: 'US - New England',        code: 'new-england',        color: MARIGOLD,       states: ['ct', 'me', 'ma', 'nh', 'ri', 'vt'],                             labelState: 'me', labelText: 'New<br/>England',           labelOffset: { x: 30, y: 20 }},
    { division: 'US - Middle Atlantic',    code: 'middle-atlantic',    color: RED_PLUM,       states: ['nj', 'ny', 'pa'],                                               labelState: 'ny', labelText: 'Middle<br/>Atlantic',        labelOffset: { y: 20, x: -10 }},
    { division: 'US - East North Central', code: 'east-north-central', color: CHARCOAL_GREY,  states: ['il', 'in', 'mi', 'oh', 'wi'],                                   labelState: 'oh', labelText: 'East North<br/>Central',    labelOffset: { x: -20 }},
    { division: 'US - West North Central', code: 'west-north-central', color: CYAN,           states: ['ia', 'ks', 'mn', 'mo', 'ne', 'nd', 'sd'],                       labelState: 'ne', labelText: 'West North<br/>Central',    labelOffset: { x: 10 }},
    { division: 'US - South Atlantic',     code: 'south-atlantic',     color: VIOLET,         states: ['de', 'fl', 'ga', 'md', 'nc', 'sc', 'va', 'dc', 'wv'],           labelState: 'nc', labelText: 'South<br/>Atlantic',         labelOffset: { x: -20, y: 15 }},
    { division: 'US - East South Central', code: 'east-south-central', color: BRIGHT_GREEN,   states: ['al', 'ky', 'ms', 'tn'],                                         labelState: 'ms', labelText: 'East South<br/>Central',    labelOffset: { x: 15 }},
    { division: 'US - West South Central', code: 'west-south-central', color: PERIWINKLE,     states: ['ar', 'la', 'ok', 'tx'],                                         labelState: 'tx', labelText: 'West South<br/>Central'},
    { division: 'US - Mountain',           code: 'mountain',           color: NAVY,           states: ['az', 'co', 'id', 'mt', 'nv', 'nm', 'ut', 'wy'],                 labelState: 'ut', labelText: 'Mountain'},
    { division: 'US - Pacific',            code: 'pacific',            color: GROSVENOR_BLUE, states: ['ak', 'ca', 'hi', 'or', 'wa'],                                   labelState: 'ca', labelText: 'Pacific'}
  ];
  noResultsText = 'No Results';
  tooltipOptions: TooltipOptions = {
    backgroundColor: ROYAL_BLUE,
    borderColor: this.whiteColor,
    borderRadius: 8,
    style: {
      color: this.whiteColor,
      fontSize: '13px',
      fontFamily: 'Gilroy, sans-serif'
    },
    useHTML: true,
    borderWidth: 0.5,
    shadow: true
  }

  getDefaultHighchartsOptions(chartType: ChartType, datasets: DataSeries[] | DataSeries): Highcharts.Options {
    const series: Highcharts.SeriesOptionsType[] = []
    if (datasets) {
      if (Array.isArray(datasets)) {
        datasets.forEach(dataSeries => {
          series.push(this.getSeriesFromDataset(chartType, dataSeries))
        })
      }
      else {
        series.push(this.getSeriesFromDataset(chartType, datasets))
      }
    }
    let sum = -1;
    if (series.length > 0) {
      sum = 0;
      (series[0] as { data: [string | number, number | null][] })['data'].map((a) => a[1]).forEach((v) => {
        sum += Math.abs(v ?? 0);
      })
      if (sum == 0) {
        // safety check to prevent divide by 0 if somehow the whole series vals are 0
        sum = -1
      }
    }
    return {
      colors: this.defaultColors,
      chart: {
        type: chartType,
        height: 200
      },
      title: {
        text: ''
      },
      xAxis: {
        type: 'category',
        title: {
          text: null
        },
        labels: {
          style: {
            fontSize: '13px'
          }
        },
        tickmarkPlacement: 'on',
        tickWidth: 1
      },
      yAxis: {
        title: {
          text: null
        },
        gridLineColor: '#000',
        gridLineWidth: 0,
        labels: {
          enabled: false
        },
        plotLines: [{
          color: 'rgb(204, 214, 235)',
          width: 1,
          value: 0
        }]
      },
      legend: {
        enabled: false
      },
      tooltip: {
        enabled: true,
        ...this.tooltipOptions
      },
      plotOptions: {
        series: {
          dataLabels: {
            enabled: true,
            formatter: function () {
              const valToShow = ((Math.abs((this as unknown as { y: number }).y) / Number(sum)) * 100).toFixed(1) + '%';
              return valToShow;
            },
            style: {
              fontSize: '13px'
            },
            crop: false
          }
        }, bar: {
          pointWidth: 20
        }
      },
      series: series
    } as unknown as Highcharts.Options;
  }

  getMapHighchartsOptions(datasets: Record<string, number>[], currency = 'USD'): Highcharts.Options {
    const currencyFormat = this.currencyFormat;
    const whiteColor = this.whiteColor;
    return {
      chart: {
        map: topologyJson,
        height: 340
      },
      title: {
        text: ''
      },
      tooltip: {
        ...this.tooltipOptions,
        formatter: function () {
          type MapPoint = Highcharts.Point & { actualValue: number; 'hc-key': string };
          if ((this.series.data.filter(data => data.name === this.key)[0] as MapPoint)['actualValue'] !== 0) {
            const seriesData = this.series.data.filter(data => data.name === this.key)[0] as MapPoint;
            const regionVal = ['oc', 'af', 'sa'].includes(seriesData['hc-key'].toString()) ? 'Others' : this.key
            const valToShow = currencyFormat.transform(seriesData['actualValue'], currency);
            const data = `Remaining Market Value <br/>` + valToShow + ' <br/><hr class="mt-2 mb-2 mr-0 ml-0" style="background-color: ' + whiteColor + '">'
            const footer = `<div>${regionVal}</div>`
            return data + footer
          }
          else {
            return false;
          }
        },
        hideDelay: 100,
        outside: true,
        backgroundColor: ROYAL_BLUE
      },
      legend: {
        enabled: false
      },
      colorAxis: {
        min: 0
      },
      series: [{
        data: datasets,
        type: 'map',
        states: {
          hover: {
            enabled: false
          }
        },
        dataLabels: {
          enabled: true,
          color: whiteColor,
          borderColor: whiteColor,
          formatter: function () {
            const pt = (this as unknown as { point: { displayName?: string; 'hc-key'?: string; value: number } }).point;
            return `<div id="GeographyLabel_${(pt['displayName'] ?? pt['hc-key'] ?? '').toString().toUpperCase()}" style="font-size: 0.95vh; text-align:center"><div style="font-weight: 700;">${pt.value}%</div><div style="text-transform: uppercase; font-weight: 400;">${pt['displayName'] ?? pt['hc-key']}</div></div>`
          },
          useHTML: true
        }
      }]
    }
  }

  getUsMapHighchartsOptions(datasets: Record<string, unknown>[], currency = 'USD', narrowWidth = 200): Highcharts.Options {
    const currencyFormat = this.currencyFormat;
    const whiteColor = this.whiteColor;
    return {
      chart: {
        map: usTopology,
        height: 340,
        marginRight: 40,
        marginLeft: 25
      },
      title: { text: '' },
      tooltip: {
        ...this.tooltipOptions,
        formatter: function () {
          const point = (this as unknown as { point: { actualValue: number; regionName: string; value: number } }).point;
          if (!point['actualValue'] || point['actualValue'] === 0) return false;
          const valToShow = currencyFormat.transform(point['actualValue'], currency);
          const chartWidth = (this as unknown as { series?: { chart?: { chartWidth?: number } } }).series?.chart?.chartWidth ?? 0;
          const valueDisplay = chartWidth < narrowWidth ? `${valToShow} | ${point['value']}%` : valToShow;
          return `Remaining Market Value <br/>${valueDisplay} <br/><hr class="mt-2 mb-2 mr-0 ml-0" style="background-color: ${whiteColor}"><div>${point['regionName']}</div>`;
        },
        hideDelay: 100,
        outside: true,
        backgroundColor: ROYAL_BLUE
      },
      legend: { enabled: false },
      colorAxis: { visible: false },
      series: [{
        data: datasets,
        type: 'map',
        borderWidth: 0, // suppress state borders; division color fills communicate grouping
        states: { hover: { enabled: false } },
        dataLabels: {
          enabled: true,
          crop: false,
          allowOverlap: true,
          style: { fontSize: '0.55em' },
          formatter: function (this: Highcharts.Point & { displayLabel?: string | null; labelOffset?: { x?: number; y?: number } | null }) {
            const displayLabel = this.displayLabel;
            if (!displayLabel) return false as unknown as string;

            const chartWidth = (this as unknown as { series?: { chart?: { chartWidth?: number } } }).series?.chart?.chartWidth ?? 0;
            if (chartWidth < narrowWidth) {
              return `<div style="width:8px;height:8px;border-radius:50%;background:rgba(255,255,255,0.88);border:1.5px solid rgba(0,0,0,0.15);"></div>`;
            }

            const off = this.labelOffset;
            const translate = off ? `transform:translate(${off.x ?? 0}px,${off.y ?? 0}px);` : '';
            const color = displayLabel === 'New<br/>England' ? CHARCOAL_GREY : whiteColor;
            return `<div style="${translate}text-align:center;color:${color}"><div style="font-weight:700;">${this.value}%</div><div style="text-transform:uppercase;font-weight:400;">${displayLabel}</div></div>`;
          },
          useHTML: true
        }
      }]
    };
  }

  getSeriesFromDataset(seriesType: ChartType, dataSeries: DataSeries): Highcharts.SeriesOptionsType {
    let values: [string | number, number][] = [];
    if (dataSeries && dataSeries.data) {
      values = dataSeries.data.map(kvp => [kvp.key, kvp.value]);
    }
    return { name: dataSeries?.name, type: seriesType, data: values } as Highcharts.SeriesOptionsType;
  }

  buildDataSeries(widget: { key: string | number; value: number }[], name: string, errorMessage: string = this.noResultsText): DataSeries {
    if (widget && widget.length > 0) {
      return { name, data: widget, error: '' };
    }
    else {
      return {
        name,
        data: null as unknown as KeyValuePair<string|number,number>[], error: errorMessage
      };
    }
  }
}